/**
 * Extracted from CartPage.jsx — the address-picker bottom drawer +
 * the coins-redeem slider block. Two small but cohesive pieces;
 * grouped to keep ``_partials/`` from blowing up with one-shot files.
 */
import { MapPin, Home, Briefcase, Coins } from "lucide-react";
import { inr2 } from "../../../lib/api";

const LABEL_ICON = { home: Home, work: Briefcase };

export function AddressPickerDrawer({
    addrs, selectedAddrId, onPick, onAddNew, onClose,
}) {
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={onClose}>
            <div className="bg-card w-full max-w-md md:max-w-lg mx-auto rounded-t-3xl p-4" onClick={(e) => e.stopPropagation()}>
                <p className="font-bold text-base mb-2">Select delivery address</p>
                <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                    {addrs.map((a) => {
                        const I = LABEL_ICON[a.label] || MapPin;
                        return (
                            <button
                                key={a.id}
                                data-testid={`addr-pick-${a.id}`}
                                onClick={() => onPick(a.id)}
                                className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border ${selectedAddrId === a.id ? "border-primary bg-primary/5" : "border-border"}`}
                            >
                                <I size={18} className="mt-0.5 text-primary" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-sm capitalize">{a.label}</p>
                                    <p className="text-xs text-muted-foreground line-clamp-2">{a.line1}{a.line2 ? `, ${a.line2}` : ""} • {a.city} {a.pincode}</p>
                                </div>
                            </button>
                        );
                    })}
                    {addrs.length === 0 && <p className="text-sm text-muted-foreground">No saved addresses yet.</p>}
                </div>
                <button
                    data-testid="cart-add-new-addr"
                    onClick={onAddNew}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-muted rounded-xl px-3 py-3 font-semibold text-sm"
                ><MapPin size={14} /> Add new address</button>
            </div>
        </div>
    );
}

export function CoinsRedeemBlock({
    coinsBalance, coinsConfig, coinsToRedeem, setCoinsToRedeem,
    coinMax, coinMin, coinsDisc, subtotalForCap,
}) {
    if (!(coinsConfig?.enabled && coinsBalance > 0)) return null;

    const rupeePerCoin = Number(coinsConfig?.rupee_per_coin || 1);
    const pct = Math.max(1, Number(coinsConfig?.max_redeem_pct_of_order || 100));
    const minRupees = coinMin * rupeePerCoin;
    // Cart total needed so that the per-order PERCENT cap allows >= coinMin
    // coins worth of discount. e.g. pct=20, coinMin=300, rupeePerCoin=1 →
    // need subtotal ≥ ₹1500.
    const minRupeesForPct = Math.ceil((coinMin * rupeePerCoin * 100) / pct);
    const minRupeesNeeded = Math.max(minRupees, minRupeesForPct);
    const cartShort = Math.max(0, minRupeesNeeded - (subtotalForCap || 0));
    const coinShort = Math.max(0, coinMin - coinsBalance);
    const tooLow = coinMax < coinMin;

    return (
        <div className="space-y-2 pt-3 mt-3 border-t border-border" data-testid="coins-redeem-block">
            <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
                    <Coins size={12} className="text-amber-500" /> Redeem coins
                </label>
                <span className="text-[11px] text-muted-foreground">
                    Balance: <span className="font-bold text-foreground" data-testid="coins-balance">{coinsBalance}</span>
                </span>
            </div>
            {tooLow ? (
                coinsBalance < coinMin ? (
                    <p className="text-[11px] text-muted-foreground" data-testid="coins-too-low">
                        Earn <b>{coinShort} more coins</b> to start redeeming (minimum {coinMin}).
                    </p>
                ) : cartShort > 0 ? (
                    <p className="text-[11px] text-muted-foreground" data-testid="coins-too-low">
                        Add <b>{inr2(cartShort)} more</b> to your cart to redeem {coinMin} coins.
                        <span className="block opacity-70 mt-0.5">
                            Max {pct}% of order value can be paid with coins.
                        </span>
                    </p>
                ) : (
                    <p className="text-[11px] text-muted-foreground" data-testid="coins-too-low">
                        You can&apos;t redeem coins on this order — the {pct}% cap is below the {coinMin}-coin minimum.
                    </p>
                )
            ) : (
                <>
                    <div className="flex items-center gap-3">
                        <input
                            data-testid="coins-slider"
                            type="range"
                            min={0}
                            max={coinMax}
                            step={10}
                            value={Math.min(coinsToRedeem, coinMax)}
                            onChange={(e) => {
                                const v = Number(e.target.value);
                                setCoinsToRedeem(v > 0 && v < coinMin ? 0 : v);
                            }}
                            className="flex-1 accent-amber-500"
                        />
                        <div className="text-right min-w-[5rem]">
                            <p className="text-sm font-bold text-amber-600" data-testid="coins-redeem-count">{coinsToRedeem}</p>
                            <p className="text-[10px] text-muted-foreground">coins</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="text-muted-foreground">
                            Up to {coinMax} coins ({coinsConfig.max_redeem_pct_of_order}% cap)
                        </span>
                        {coinsDisc > 0 && (
                            <span className="text-emerald-700 font-bold" data-testid="coins-discount-preview">
                                −{inr2(coinsDisc)} off
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2 pt-1">
                        <button
                            type="button"
                            data-testid="coins-redeem-max"
                            onClick={() => setCoinsToRedeem(coinMax)}
                            className="flex-1 text-[11px] font-semibold bg-amber-50 border border-amber-200 text-amber-700 rounded-lg py-1.5"
                        >Use max ({coinMax})</button>
                        {coinsToRedeem > 0 && (
                            <button
                                type="button"
                                data-testid="coins-redeem-clear"
                                onClick={() => setCoinsToRedeem(0)}
                                className="flex-1 text-[11px] font-semibold bg-muted text-foreground rounded-lg py-1.5"
                            >Clear</button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
